import { RegistrationOfEnergyProducerApplication } from './../entities/regep-application';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GridApplicationService } from 'src/app/+grid-application-shared/services/grid-application.service';
import { CommunicationService } from 'src/app/services/communication.service';
import { UrlSegments } from 'src/app/jurisdiction/jurisdiction.constants';
import { AddressAndPersonalData } from '../../+grid-application-shared/entities/personal-data/addressAndPersonalData';
import { UpdateApplicationStatusRequest } from 'src/app/jurisdiction/jurisdiction.domain';
import { map } from 'rxjs/operators';
import { RegepTechnicalSpecificationsSectionsState } from '../entities/sections-state/regepTechnicalSpecificationsSectionsState';
import { PersonalDataSectionsState } from 'src/app/+grid-application-shared/entities/sections-state/personalDataSectionsState';
import { DocumentsSectionsState } from 'src/app/+grid-application-shared/entities/sections-state/documentsSectionsState';
import {
  ENERGY_SOURCES,
  ENERGY_TYPES,
  MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION,
} from 'src/app/+grid-application-shared/constants/grid-application-constants';
import { RegepProcessStatus } from '../entities/enums/regepProcessStatus';
import { RegistrationOfEnergyProducerRegulatoryData } from '../entities/regep-regulatory-data';
import { RegepRegulatoryDataSectionsState } from '../entities/sections-state/regepRegulatoryDataSectionsState';
import { UserService } from 'src/app/services/user.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class RegepApplicationService extends GridApplicationService<RegistrationOfEnergyProducerApplication> {
  private readonly operatorAddressUrl: string = UrlSegments.RegepCreatePowerPlantOperatorAddress;
  private readonly regulatoryDataUrl: string = UrlSegments.RegistrationOfEnergyProducerRegulatoryData;

  constructor(
    communicationService: CommunicationService,
    private userService: UserService,
    private localStorageService: LocalStorageService
  ) {
    super(communicationService, {
      technicalData: UrlSegments.RegistrationOfEnergyProducerTechnicalData,
      uploadDocument: UrlSegments.UploadRegistrationOfEnergyProducerDocuments,
    });
  }

  public areRequiredSectionsReviewed(applicationState: RegepProcessStatus): boolean {
    let sectionsReviewed: boolean = false;

    if ([RegepProcessStatus.RequestSent, RegepProcessStatus.EngineeringRequestUpdated].includes(applicationState)) {
      sectionsReviewed = this.isTechnicianDataReviewed();
    } else if ([RegepProcessStatus.CustomerDataAdded, RegepProcessStatus.RegulationsRequestUpdated].includes(applicationState)) {
      sectionsReviewed = this.isCustomerDataReviewed();
    }

    return sectionsReviewed;
  }

  public isRegulatoryDataPending(): boolean {
    const regepApplication: RegistrationOfEnergyProducerApplication = this.application;

    return Object.keys(regepApplication.regulatory_data).length === 0;
  }

  private isTechnicianDataReviewed(): boolean {
    const regepApplication: RegistrationOfEnergyProducerApplication = this.application;

    if (
      JSON.parse(String(regepApplication.technical_data.has_energy_producer)) &&
      regepApplication.technical_specifications_sections_state.is_existing_energy_producer_locked === null
    ) {
      return false;
    }

    if (
      ENERGY_TYPES[regepApplication.technical_data.existing_energy_producers[0].energy_type] === ENERGY_TYPES.PHOTOVOLTAICS &&
      regepApplication.technical_specifications_sections_state.is_detailed_info_photovoltaics_locked === null
    ) {
      return false;
    }

    if (
      ENERGY_TYPES[regepApplication.technical_data.existing_energy_producers[0].energy_type] > ENERGY_TYPES.STORAGE &&
      regepApplication.technical_specifications_sections_state.is_detailed_info_energy_producer_locked === null
    ) {
      return false;
    }

    if (
      (ENERGY_TYPES[regepApplication.technical_data.existing_energy_producers[0].energy_type] === ENERGY_TYPES.STORAGE ||
        JSON.parse(String(regepApplication.technical_data.has_power_storage))) &&
      regepApplication.technical_specifications_sections_state.is_detailed_info_storage_locked === null
    ) {
      return false;
    }

    if (
      !JSON.parse(String(regepApplication.businesspartner.owner_is_invoice_recepient)) &&
      regepApplication.personal_data_sections_state.is_invoice_address_locked === null
    ) {
      return false;
    }

    if (
      !JSON.parse(String(regepApplication.businesspartner.owner_is_pp_operator)) &&
      regepApplication.personal_data_sections_state.is_operator_of_power_plant_locked === null
    ) {
      return false;
    }

    const totalFeedInPower = regepApplication.technical_data.existing_energy_producers[0].total_feed_in_power;
    const areDocsUploaded = !!regepApplication.technical_data.grid_power_plant_protection_data.integrated_grid_protection_documents?.length;
    if (
      ((totalFeedInPower <= MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION && areDocsUploaded) ||
        totalFeedInPower > MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION) &&
      regepApplication.technical_specifications_sections_state.is_detailed_info_grid_power_plant_locked === null
    ) {
      return false;
    }

    if (
      regepApplication.technical_data.commissioning_of_energy_producer &&
      regepApplication.technical_specifications_sections_state.is_commissioning_of_energy_producer_locked === null
    ) {
      return false;
    }

    if (
      regepApplication.technical_data.meter_operation === 'localOperator' &&
      regepApplication.technical_specifications_sections_state.is_provisioning_of_current_transformers_locked === null
    ) {
      return false;
    }

    return (
      regepApplication.technical_specifications_sections_state.is_base_data_locked !== null &&
      regepApplication.technical_specifications_sections_state.is_type_of_feed_in_locked !== null &&
      regepApplication.technical_specifications_sections_state.is_feed_in_controllability_locked !== null &&
      regepApplication.technical_specifications_sections_state.is_concept_of_measurement_locked !== null &&
      regepApplication.technical_specifications_sections_state.is_meter_operation_locked !== null &&
      regepApplication.personal_data_sections_state.is_connection_address_locked !== null &&
      regepApplication.personal_data_sections_state.is_subscriber_owner_locked !== null &&
      regepApplication.documents_sections_state?.is_documents_locked !== null
    );
  }

  private isCustomerDataReviewed(): boolean {
    if (this.isTechnicianDataReviewed() === false) {
      return false;
    }

    const regepApplication: RegistrationOfEnergyProducerApplication = this.application;
    const isWaiverCompensation: boolean = !!JSON.parse(String(regepApplication.regulatory_data.is_waiver_compensation));

    if (!isWaiverCompensation) {
      if (regepApplication.regulatory_data_sections_state.is_models_for_feed_in_compensation_locked === null) {
        return false;
      }

      if (regepApplication.regulatory_data_sections_state.is_bank_account_details_locked === null) {
        return false;
      }

      if (regepApplication.regulatory_data_sections_state.is_declaration_on_vat_payment_locked === null) {
        return false;
      }
    }

    if (
      [ENERGY_SOURCES.NATURAL_GAS, ENERGY_SOURCES.OTHER].includes(
        regepApplication.technical_data.existing_energy_producers?.[0]?.energy_source
      ) &&
      regepApplication.regulatory_data_sections_state.is_bafa_authorization_notice_locked === null
    ) {
      return false;
    }

    return (
      regepApplication.regulatory_data_sections_state.is_waiver_of_feed_in_compensation_locked !== null &&
      regepApplication.regulatory_data_sections_state.is_marktstammdatenregister_registration_locked !== null
    );
  }

  protected fetchApplication(applicationId: number): Observable<RegistrationOfEnergyProducerApplication> {
    return this.communicationService
      .getData<RegistrationOfEnergyProducerApplication>(`${UrlSegments.RegistrationOfEnergyProducerApplication}/${applicationId}`)
      .pipe(
        map(response => {
          const application = response;

          application.technical_specifications_sections_state =
            Object.keys(application.technical_specifications_sections_state).length === 0
              ? new RegepTechnicalSpecificationsSectionsState()
              : new RegepTechnicalSpecificationsSectionsState(application.technical_specifications_sections_state);

          application.personal_data_sections_state =
            Object.keys(application.personal_data_sections_state).length === 0
              ? new PersonalDataSectionsState(null)
              : new PersonalDataSectionsState(application.personal_data_sections_state);

          application.documents_sections_state =
            Object.keys(application.documents_sections_state).length === 0
              ? new DocumentsSectionsState(null)
              : new DocumentsSectionsState(application.documents_sections_state);

          application.regulatory_data_sections_state =
            Object.keys(application.regulatory_data_sections_state).length === 0
              ? new RegepRegulatoryDataSectionsState(null)
              : new RegepRegulatoryDataSectionsState(application.regulatory_data_sections_state);

          return application;
        })
      );
  }

  public createRegulatoryData(
    regulatoryData: RegistrationOfEnergyProducerRegulatoryData
  ): Observable<RegistrationOfEnergyProducerRegulatoryData> {
    return this.communicationService.postData(this.regulatoryDataUrl, regulatoryData);
  }

  public updateRegulatoryData(
    regulatoryData: RegistrationOfEnergyProducerRegulatoryData
  ): Observable<RegistrationOfEnergyProducerRegulatoryData> {
    return this.communicationService.updateData(this.regulatoryDataUrl, regulatoryData);
  }

  public createPpOperatorAddress(address: AddressAndPersonalData): Observable<any> {
    return this.communicationService.postData(this.operatorAddressUrl, address);
  }

  public updatePpOperatorAddress(address: AddressAndPersonalData): Observable<any> {
    return this.communicationService.updateData(this.operatorAddressUrl, address);
  }

  public deletePpOperatorAddress(operatorAddressId: number): Observable<any> {
    return this.communicationService.deleteData(`${this.operatorAddressUrl}?id=${operatorAddressId}`);
  }

  public updateRegepApplication(updateRequest: UpdateApplicationStatusRequest, applicationId: number): Observable<any> {
    return this.communicationService.updateData(`${UrlSegments.RegistrationOfEnergyProducerApplication}/${applicationId}`, updateRequest);
  }

  public downloadExport(applicationId: number, contentType: string, language: string): Observable<any> {
    const url = UrlSegments.RegistrationOfEnergyProducerExport.replace(':applicationId', applicationId.toString());
    const options = {
      headers: {
        'Content-Type': contentType,
        'Accept-Language': language,
      },
    };

    return this.communicationService.getBlobData(url, options);
  }

  public get isApplicationCustomer(): boolean {
    const technicianId = this.localStorageService.getItem('technician_id');

    return this.userService.isUserCustomerRole() && this.application?.customer_id?.toString() === technicianId;
  }

  public get isCustomerAsApplicationTechnician(): boolean {
    const technicianId = this.localStorageService.getItem('technician_id');

    return this.userService.isUserCustomerRole() && this.application?.technician_id === technicianId;
  }
}
